// Global criteria tab: one card per GLOBAL_CRITERIA entry. Any number of cards
// can be open; a click on a card's title opens or closes it. The card last
// touched is the "focused" one — the canvas outlines the tiles it reaches.

import type { CSSProperties } from 'react'
import { Calendar, Hash, Type, Plus, Check } from 'lucide-react'
import type { ProjectVariable } from '@/api/projectVariables'
import type {
  DashboardBody, DashboardDataType, DashboardGlobalCriterion, DashboardOperator,
} from './types'
import CriteriaLookupItemChange from '../visual/CriteriaLookupItemChange'
import CriteriaDefaultField from '../visual/CriteriaDefaultField'
import CriteriaValidationsEditor from '../visual/CriteriaValidationsEditor'
import { Chevron, Switch } from './designerShared'

const OPERATORS: Array<{ value: DashboardOperator; label: string }> = [
  { value: '=', label: '= equals' },
  { value: '!=', label: '≠ not equal' },
  { value: '>', label: '> after / above' },
  { value: '>=', label: '≥ on or after' },
  { value: '<', label: '< before / below' },
  { value: '<=', label: '≤ on or before' },
  { value: 'between', label: 'between' },
  { value: 'in', label: 'in (one of)' },
  { value: 'like', label: 'like (contains)' },
]
const DATA_TYPES: Array<{ value: DashboardDataType; label: string }> = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
]

// The look of .dd-inp for the shared editors that take inline styles.
const INPUT: CSSProperties = {
  width: '100%', height: 40, border: '1px solid var(--color-border)', borderRadius: 10, padding: '0 12px',
  background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: 14,
}

const normName = (s: string) => (s || '').trim().replace(/\s+/g, '_')

export interface CriteriaPanelProps {
  canEdit: boolean
  project: string
  projectVars: ProjectVariable[]
  body: DashboardBody
  open: ReadonlySet<number>
  focused: number | null
  onToggle: (idx: number) => void
  onToggleAll: () => void
  onAdd: () => void
  onDelete: (idx: number) => void
  onChange: <K extends keyof DashboardGlobalCriterion>(idx: number, field: K, value: DashboardGlobalCriterion[K]) => void
  onToggleBroadcast: (idx: number, visualId: string) => void
}

export default function CriteriaPanel(p: CriteriaPanelProps) {
  const list = p.body.GLOBAL_CRITERIA
  const allOpen = list.length > 0 && list.every((_, i) => p.open.has(i))
  return (
    <>
      <div className="dd-phead">
        <div className="t">
          <div className="dd-ptitle">Global criteria</div>
          <p className="dd-hint" style={{ marginTop: 4 }}>
            The filters on the card at the top. One value reaches every tile it is bound to.
          </p>
        </div>
        {list.length > 0 && (
          <button className="dd-tb link" style={{ height: 34 }} onClick={p.onToggleAll}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
        {p.canEdit && (
          <button className="dd-tb pri" style={{ height: 34 }} onClick={p.onAdd}><Plus size={15} /> Add</button>
        )}
      </div>
      <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.map((c, i) => (
          <CriterionCard key={i} {...p} c={c} idx={i} isOpen={p.open.has(i)} isFocused={p.focused === i} />
        ))}
        {list.length === 0 && (
          <p className="dd-hint">No global filters. Add one to send a value to several tiles at once.</p>
        )}
      </div>
    </>
  )
}

function CriterionCard(p: CriteriaPanelProps & {
  c: DashboardGlobalCriterion; idx: number; isOpen: boolean; isFocused: boolean
}) {
  const { c, idx, canEdit, body } = p
  const set = <K extends keyof DashboardGlobalCriterion>(f: K, v: DashboardGlobalCriterion[K]) => p.onChange(idx, f, v)
  const reaches = body.VISUALS.filter((t) => {
    const binds = t.CRITERIA_BINDINGS || []
    return binds.length
      ? binds.some((b) => b.BIND_TYPE === 'global' && b.GLOBAL_PARAM === c.PARAM_NAME)
      : c.BROADCAST_TO.includes(t.VISUAL_ID)
  }).length
  const glyph = c.DATA_TYPE === 'date'
    ? <span className="dd-glyph date"><Calendar size={17} /></span>
    : c.DATA_TYPE === 'number'
      ? <span className="dd-glyph num"><Hash size={17} /></span>
      : <span className="dd-glyph text"><Type size={17} /></span>
  const def = (c.DEFAULT_VALUE || '').trim()
  const summary = [
    c.PARAM_NAME || '(no param name)',
    DATA_TYPES.find((d) => d.value === c.DATA_TYPE)?.label.toLowerCase(),
    c.OPERATOR,
    def ? `default ${def}` : null,
    c.mandatory ? 'required' : null,
  ].filter(Boolean).join(' · ')
  const inputType = c.DATA_TYPE === 'date' ? 'date' : c.DATA_TYPE === 'number' ? 'number' : 'text'
  const id = `dd-g${idx}`

  return (
    <div className={`dd-fcard${p.isOpen ? ' open' : ''}${p.isFocused ? ' on' : ''}`}>
      <button className="dd-fcard-h" onClick={() => p.onToggle(idx)} aria-expanded={p.isOpen}>
        {glyph}
        <span className="t"><b>{c.PROMPT_LABEL || c.PARAM_NAME || 'New criterion'}</b><span className="dd-mono">{summary}</span></span>
        <span className="dd-cnt" title="Tiles this criterion reaches">→ {reaches}</span>
        <Chevron open={p.isOpen} />
      </button>
      {p.isOpen && (
        <fieldset disabled={!canEdit} className="dd-fbody"
          style={{ margin: 0, minWidth: 0, border: 'none', borderTop: '1px solid var(--color-border)' }}>
          <div className="dd-g2" style={{ marginTop: 10 }}>
            <div>
              <label className="dd-lab" htmlFor={`${id}-label`}>Prompt label</label>
              <input id={`${id}-label`} className="dd-inp" value={c.PROMPT_LABEL}
                onChange={(e) => set('PROMPT_LABEL', e.target.value)} placeholder="e.g. From date" />
            </div>
            <div>
              <label className="dd-lab" htmlFor={`${id}-name`}>Param name</label>
              <input id={`${id}-name`} className="dd-inp mono" value={c.PARAM_NAME}
                onChange={(e) => set('PARAM_NAME', e.target.value)} placeholder="e.g. from_date" />
            </div>
          </div>
          <div className="dd-g2">
            <div>
              <label className="dd-lab" htmlFor={`${id}-type`}>Data type</label>
              <select id={`${id}-type`} className="dd-inp" value={c.DATA_TYPE}
                onChange={(e) => set('DATA_TYPE', e.target.value as DashboardDataType)}>
                {DATA_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="dd-lab" htmlFor={`${id}-op`}>Operator</label>
              <select id={`${id}-op`} className="dd-inp" value={c.OPERATOR}
                onChange={(e) => set('OPERATOR', e.target.value as DashboardOperator)}>
                {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {c.OPERATOR === 'between' ? (
            // BETWEEN default = "lo,hi" (the pair the engine splits into two binds).
            <div className="dd-g2">
              {([0, 1] as const).map((pos) => {
                const parts = (c.DEFAULT_VALUE || '').split(',')
                const lo = (parts[0] ?? '').trim()
                const hi = (parts[1] ?? '').trim()
                return (
                  <div key={pos}>
                    <label className="dd-lab" htmlFor={`${id}-b${pos}`}>{pos === 0 ? 'Default from' : 'Default to'}</label>
                    <input id={`${id}-b${pos}`} className="dd-inp" type={inputType} value={pos === 0 ? lo : hi}
                      onChange={(e) => {
                        const v = e.target.value.trim()
                        const nl = pos === 0 ? v : lo
                        const nh = pos === 1 ? v : hi
                        set('DEFAULT_VALUE', nl || nh ? `${nl},${nh}` : '')
                      }} />
                  </div>
                )
              })}
            </div>
          ) : (
            <div>
              <div className="dd-lab">Default value</div>
              {/* Literal OR a :calendar / :user / :<project variable> token,
                  resolved on the server — the visual designer's control. */}
              <CriteriaDefaultField
                value={c.DEFAULT_VALUE || ''}
                projectVars={p.projectVars}
                chips={c.OPERATOR === 'in'}
                chipInputType={inputType}
                onChange={(val) => set('DEFAULT_VALUE', val)}
                inputStyle={INPUT}
                selectStyle={{ ...INPUT, paddingRight: 8 }}
              />
            </div>
          )}
          <div>
            <Switch
              on={!!c.mandatory}
              title="Mandatory"
              detail="The dashboard waits until this has a value."
              onChange={(on) => set('mandatory', (on || undefined) as DashboardGlobalCriterion['mandatory'])}
            />
            {c.mandatory && !def && (
              <p className="dd-hint">No default, so the dashboard opens with the filters showing and runs no tile until it is filled in.</p>
            )}
          </div>

          <div>
            <div className="dd-sec-h" style={{ margin: '2px 0 8px' }}>Checks</div>
            <CriteriaValidationsEditor
              project={p.project}
              label={c.PROMPT_LABEL || c.PARAM_NAME}
              criteriaNames={body.GLOBAL_CRITERIA.map((x) => normName(x.PARAM_NAME).toLowerCase()).filter(Boolean)}
              validations={c.validations}
              onChange={(next) => set('validations', (next.length ? next : undefined) as DashboardGlobalCriterion['validations'])}
            />
          </div>

          <div>
            <div className="dd-sec-h" style={{ margin: '2px 0 8px' }}>Lookup &amp; item change</div>
            <CriteriaLookupItemChange
              project={p.project}
              triggerName={normName(c.PARAM_NAME).toUpperCase()}
              otherParamNames={body.GLOBAL_CRITERIA
                .filter((_, j) => j !== idx)
                .map((x) => normName(x.PARAM_NAME).toUpperCase())
                .filter(Boolean)}
              value={{
                lookup: c.lookup,
                lookup_filter: c.lookup_filter,
                lookup_filter_rules: c.lookup_filter_rules,
                lookup_sql: c.lookup_sql,
                lookup_title: c.lookup_title,
                lookup_sql_data_col: c.lookup_sql_data_col,
                item_change: c.item_change,
                options: c.options,
              }}
              onLookupChange={(patch) => {
                for (const [k, v] of Object.entries(patch)) {
                  p.onChange(idx, k as keyof DashboardGlobalCriterion, v as never)
                }
              }}
              onItemChange={(v) => set('item_change', (v ?? null) as never)}
              onOptionsChange={(v) => set('options', (v ?? []) as never)}
            />
          </div>

          <div>
            <div className="dd-sec-h" style={{ margin: '2px 0 8px' }}>
              Broadcast to<span className="r">{reaches} of {body.VISUALS.length}</span>
            </div>
            {body.VISUALS.length === 0 ? (
              <p className="dd-hint" style={{ margin: 0 }}>No tiles yet. Add tiles on the Layout tab first.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {body.VISUALS.map((t) => {
                  const binds = t.CRITERIA_BINDINGS || []
                  const locked = binds.length > 0
                  const on = locked
                    ? binds.some((b) => b.BIND_TYPE === 'global' && b.GLOBAL_PARAM === c.PARAM_NAME)
                    : c.BROADCAST_TO.includes(t.VISUAL_ID)
                  return (
                    <button
                      key={t.VISUAL_ID}
                      type="button"
                      className="dd-conn"
                      aria-pressed={on}
                      disabled={locked || !canEdit}
                      title={locked ? 'This tile has criteria bindings — change them on the tile (Layout tab).' : undefined}
                      onClick={() => p.onToggleBroadcast(idx, t.VISUAL_ID)}
                    >
                      <span className="dd-box">{on && <Check size={12} strokeWidth={3.5} />}</span>
                      <span className="t">
                        {t.VISUAL_NAME || t.VISUAL_ID}
                        <span>{locked ? 'Set by this tile’s criteria bindings' : t.VISUAL_ID}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
            <p className="dd-hint">A tile with criteria bindings receives only what its bindings say. Set those on the tile.</p>
          </div>
          {canEdit && (
            <div style={{ display: 'flex' }}>
              <button className="dd-tb danger sm" style={{ marginLeft: 'auto' }} onClick={() => p.onDelete(idx)}>
                Remove criterion
              </button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  )
}
